import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Add } from '@mui/icons-material'

import KnowledgeService from '@/services/KnowledgeService'
import {
  getAllAgents,
  getCurrentOrganisation,
  updateSelectedAgent,
} from '@/store/agents/actions'
import {
  getOrganisationFeatures,
  getOrganizationCurrentUserDetails,
} from '@/store/common/actions'
import { getIsSuperAdmin } from '@/store/common/selector'
import { useGlobalContext } from '@/context/GlobalContext'
import { onAgentSearchHandler, selectAgent } from '@/utils/appUtils'
import ChooseAgentListItem from '@/components/UI/ChooseAgentListItem/ChooseAgentListItem'
import AddAgentPopup from '../SwitchAgent/popup/AddAgentPopup'

const ChooseAgent = ({ allAgents, agentSelected }: any) => {
  const { t } = useTranslation('translation')
  const dispatch = useDispatch()
  const history = useHistory()
  const IsSuperAdmin = useSelector(getIsSuperAdmin)
  const { setSelectedAgentName } = useGlobalContext()
  const [fetchedAgents, setFetchedAgents] = useState([])
  const [selectedAgent, setSelectedAgent] = useState(
    localStorage.getItem('selectedAgent'),
  )

  const [organizationName, setOrganizationName] = useState('')
  const [query, setQuery] = useState('')
  const [agentOrder, setAgentOrder] = useState({
    value: null,
    label: t('By Agent Name'),
  })
  const [isMultiAgentFeatureEnabled, setIsMultiAgentFeatureEnabled] =
    useState(false)
  const [showModal, setShowModal] = useState(null)
  const [currentOrganisation, setCurrentOrganisation] = useState(null)
  const [loadingAgentId, setLoadingAgentId] = useState<string | null>(null)

  const service = new KnowledgeService()

  useEffect(() => {
    fetchAllAgents()
    getCurrentOrg()
    getAgentsData()
  }, [allAgents])

  const getAgentsData = async () => {
    try {
      const features: any = await dispatch(getOrganisationFeatures())
      setIsMultiAgentFeatureEnabled(
        features.some((feature: any) => feature.name === 'multiagent'),
      )
    } catch (e: any) {
      setIsMultiAgentFeatureEnabled(false)
    }
  }

  const getCurrentOrg = async () => {
    try {
      const currentOrganisation: any = await dispatch(getCurrentOrganisation())
      setOrganizationName(currentOrganisation?.organisation?.name)
      setCurrentOrganisation(currentOrganisation?.organisation)
    } catch (e: any) {
      setOrganizationName(null)
    }
  }

  const chooseCurrentAgentHandler = async (e: any) => {
    e.preventDefault()
    const agentId = e.target.dataset.agentid
    const agentName = e.target.dataset.name

    if (!agentId || !agentName) return

    setLoadingAgentId(agentId)

    try {
      await selectAgent({
        agentId,
        agentName,
        dispatch,
        history,
        setSelectedAgentName,
        updateSelectedAgent,
      })
      setSelectedAgent(agentId)
      await dispatch(getOrganizationCurrentUserDetails(false))
      agentSelected()
    } catch (error: any) {
      // Error is already handled by the utility function (toast notification)
    } finally {
      setLoadingAgentId(null)
    }
  }

  const fetchAllAgents = async () => {
    try {
      const fetchedAgents: any = await dispatch(getAllAgents())
      setFetchedAgents(
        fetchedAgents.filter((agent: any) => agent.status !== 'inactive'),
      )
    } catch (e: any) {
      setFetchedAgents([])
    }
  }

  const onAddEditAgentHandler = async (agent: any, selectedPersona: any) => {
    const { chat_persona } = agent?.settings
    const { status } = await service.updateWebAgentSettings(
      {
        settings: {
          chat_persona:
            selectedPersona && selectedPersona.value
              ? selectedPersona.value
              : chat_persona,
        },
      },
      agent?._id,
    )
    if (status === 200) {
      setShowModal(false)
      fetchAllAgents()
    }
  }

  return (
    <>
      <div className="page-mid-wraper h-without-foter agent-list-wrapper">
        <AddAgentPopup
          show={showModal}
          onHide={() => setShowModal(false)}
          currentOrganisation={currentOrganisation}
          footerButtonAction={onAddEditAgentHandler}
        />

        <section className="h-100 w-100 background-none">
          <div className="all-knowledge-search">
            <h1 className="mb-4 ">{organizationName}</h1>
            <div className="font-18-px font-600 mb-3">
              {t('Select the agent which you would like to manage.')}
            </div>
            <div className="web-links justify-content-between d-flex align-items-center mw-100">
              <input
                type="text"
                className="form-control h-auto"
                placeholder={t('Search Agent Name')}
                onChange={(e: any) => setQuery(e.target.value)}
              />
              <div className="doc-select-dropdown ml-3">
                <Select
                  className="select-sort select-target select-blue shadow-none radius-8"
                  classNamePrefix="select-target-prefix"
                  isSearchable={false}
                  components={{
                    IndicatorSeparator: () => null,
                  }}
                  placeholder={t('Status')}
                  options={[
                    {
                      value: null,
                      label: t('By Agent Name'),
                    },
                    {
                      value: 'date',
                      label: t('Date Created'),
                    },
                  ]}
                  onChange={(e: any) => {
                    setAgentOrder(e)
                  }}
                  value={agentOrder}
                />
              </div>
            </div>
            <div className="row">
              {IsSuperAdmin && isMultiAgentFeatureEnabled && (
                <div className="col-md-4 col-sm-6 col-xl-3 mb-3">
                  <div
                    className="knowledge-box d-flex justify-content-center align-items-center not-hover build-knowbox"
                    onClick={() => setShowModal(true)}
                    style={{
                      minHeight: '280px',
                    }}
                  >
                    <div className="text-center">
                      <Add
                        sx={{
                          fontSize: 64,
                          color: 'primary.main',
                          mb: 1,
                        }}
                      />
                      <h2 className="mb-0 pt-3 clr-primary">
                        {t('Add New Agent')}
                      </h2>
                    </div>
                  </div>
                </div>
              )}
              <>
                {fetchedAgents &&
                  fetchedAgents.length > 0 &&
                  onAgentSearchHandler(fetchedAgents, query, agentOrder).map(
                    (
                      {
                        _id,
                        name,
                        description,
                        created_at,
                        updated_at,
                        language_code,
                        settings,
                      }: any,
                      index: number,
                    ) => {
                      return (
                        <ChooseAgentListItem
                          key={_id + '_' + index}
                          selectedAgent={selectedAgent}
                          _id={_id}
                          name={name}
                          description={description}
                          created_at={created_at}
                          updated_at={updated_at}
                          chooseCurrentAgentHandler={chooseCurrentAgentHandler}
                          isMultiAgentFeatureEnabled={
                            isMultiAgentFeatureEnabled
                          }
                          hideOptions={true}
                          language_code={
                            settings?.dialect_language_code || language_code
                          }
                          isLoading={loadingAgentId === _id}
                        />
                      )
                    },
                  )}
              </>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default ChooseAgent
